import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Square, Drum, Music, Piano, Mic, AlertCircle, Repeat, RotateCcw } from 'lucide-react';

// Audio backing track player. Loads pre-rendered stems as HTML5 <audio>
// elements and plays them in lockstep. Resilient to missing / empty /
// failing stems: each stem tracks its own status, the player becomes
// usable once at least one stem is ready, and stems that 404 or decode
// to silence (< 1 second of audio) get hidden from the UI.

const STEM_META = {
  drums:  { labelKey: 'songs.stemDrums',  Icon: Drum,  defaultOn: true,  order: 0 },
  bass:   { labelKey: 'songs.stemBass',   Icon: Music, defaultOn: true,  order: 1 },
  other:  { labelKey: 'songs.stemOther',  Icon: Piano, defaultOn: true,  order: 2 },
  vocals: { labelKey: 'songs.stemVocals', Icon: Mic,   defaultOn: false, order: 3 },
};

const MIN_USEFUL_DURATION = 1.0; // seconds

const formatTime = (secs) => {
  if (!Number.isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioBackingTrack = ({
  stems = {},
  attribution = null,
  accent = 'amber',
  loop = false,
}) => {
  const { t } = useTranslation();
  const requestedKeys = Object.keys(stems).filter(k => stems[k] && STEM_META[k]);

  const [stemState, setStemState] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [stemOn, setStemOn] = useState({});
  const [volume, setVolume] = useState(0.85);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1.0);
  const [loopA, setLoopA] = useState(null);
  const [loopB, setLoopB] = useState(null);

  const audioRefs = useRef({});
  const rafRef = useRef(null);
  const loopARef = useRef(null);
  const loopBRef = useRef(null);
  useEffect(() => { loopARef.current = loopA; }, [loopA]);
  useEffect(() => { loopBRef.current = loopB; }, [loopB]);

  useEffect(() => {
    if (requestedKeys.length === 0) return undefined;

    const initial = {};
    const initialToggle = {};
    for (const k of requestedKeys) {
      initial[k] = { status: 'loading', duration: 0, errorCode: null };
      initialToggle[k] = STEM_META[k].defaultOn;
    }
    setStemState(initial);
    setStemOn(initialToggle);
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);

    let cancelled = false;

    for (const key of requestedKeys) {
      const a = new Audio();
      a.preload = 'auto';
      a.loop = loop;
      a.muted = !STEM_META[key].defaultOn;
      a.volume = volume;
      a.playbackRate = speed;
      a.preservesPitch = true;
      a.mozPreservesPitch = true;
      a.webkitPreservesPitch = true;
      a.src = stems[key];

      a.addEventListener('loadedmetadata', () => {
        if (cancelled) return;
        const dur = a.duration;
        if (!Number.isFinite(dur) || dur < MIN_USEFUL_DURATION) {
          setStemState(prev => ({ ...prev, [key]: { status: 'empty', duration: dur || 0, errorCode: null } }));
          return;
        }
        setStemState(prev => ({ ...prev, [key]: { status: 'ready', duration: dur, errorCode: null } }));
        setDuration(d => (dur > d ? dur : d));
      });

      a.addEventListener('error', () => {
        if (cancelled) return;
        const code = a.error ? a.error.code : null;
        setStemState(prev => ({ ...prev, [key]: { status: 'failed', duration: 0, errorCode: code } }));
      });

      audioRefs.current[key] = a;
    }

    return () => {
      cancelled = true;
      for (const k of Object.keys(audioRefs.current)) {
        const a = audioRefs.current[k];
        try { a.pause(); a.src = ''; a.load(); } catch (_) {}
      }
      audioRefs.current = {};
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(stems), loop]);

  useEffect(() => {
    for (const k of requestedKeys) {
      const a = audioRefs.current[k];
      if (a) a.volume = volume;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume]);

  useEffect(() => {
    for (const k of requestedKeys) {
      const a = audioRefs.current[k];
      if (a) a.muted = !stemOn[k];
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stemOn]);

  useEffect(() => {
    for (const k of requestedKeys) {
      const a = audioRefs.current[k];
      if (a) a.playbackRate = speed;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed]);

  const readyKeys = requestedKeys.filter(k => stemState[k]?.status === 'ready');
  const unavailableKeys = requestedKeys.filter(k => {
    const s = stemState[k]?.status;
    return s === 'failed' || s === 'empty';
  });
  const loadingKeys = requestedKeys.filter(k => !stemState[k] || stemState[k].status === 'loading');
  const primaryKey = readyKeys.sort((a, b) => STEM_META[a].order - STEM_META[b].order)[0];

  const trackProgress = useCallback(() => {
    const primary = audioRefs.current[primaryKey];
    if (!primary) return;
    const a = loopARef.current;
    const b = loopBRef.current;
    if (a != null && b != null && b > a && primary.currentTime >= b) {
      for (const k of Object.keys(audioRefs.current)) {
        const stem = audioRefs.current[k];
        if (stem) stem.currentTime = a;
      }
      setCurrentTime(a);
    } else {
      setCurrentTime(primary.currentTime);
    }
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(trackProgress);
    }
  }, [isPlaying, primaryKey]);

  useEffect(() => {
    if (isPlaying) {
      rafRef.current = requestAnimationFrame(trackProgress);
    } else if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying, trackProgress]);

  useEffect(() => {
    if (!primaryKey) return undefined;
    const primary = audioRefs.current[primaryKey];
    if (!primary) return undefined;
    const onEnded = () => { setIsPlaying(false); setCurrentTime(0); };
    primary.addEventListener('ended', onEnded);
    return () => primary.removeEventListener('ended', onEnded);
  }, [primaryKey]);

  const start = async () => {
    const audios = readyKeys.map(k => audioRefs.current[k]).filter(Boolean);
    if (audios.length === 0) return;
    try {
      await Promise.all(audios.map(a => a.play().catch(e => { /* swallow individual stem play errors */ })));
      setIsPlaying(true);
    } catch (_) {
      /* swallow */
    }
  };

  const stop = () => {
    for (const k of readyKeys) {
      const a = audioRefs.current[k];
      if (a) {
        a.pause();
        a.currentTime = 0;
      }
    }
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const togglePlay = () => (isPlaying ? stop() : start());

  const seek = (newTime) => {
    for (const k of readyKeys) {
      const a = audioRefs.current[k];
      if (a) a.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const accentBtn = {
    amber: 'from-amber to-orange-500',
    blue: 'from-blue to-cyan-400',
    purple: 'from-purple-500 to-pink-500',
  }[accent] || 'from-amber to-orange-500';

  if (requestedKeys.length === 0) {
    return (
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4 text-zinc-400 text-sm">
        {t('songs.noAudioBacking', { defaultValue: 'No audio backing available for this song yet.' })}
      </div>
    );
  }

  const stillLoading = loadingKeys.length > 0;
  const canPlay = readyKeys.length > 0;
  const showSoloLabel = readyKeys.length === 1 && readyKeys[0] === 'other';

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 px-5 py-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={togglePlay}
            disabled={!canPlay}
            className={`w-11 h-11 rounded-full bg-gradient-to-r ${accentBtn} text-black flex items-center justify-center hover:scale-105 transition-transform shrink-0 disabled:opacity-40 disabled:cursor-not-allowed`}
            aria-label={isPlaying ? 'Stop backing track' : 'Start backing track'}
          >
            {isPlaying ? <Square className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
          </button>
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t('songs.audioBacking', { defaultValue: 'Audio backing' })}
            </span>
            <span className="font-mono text-amber text-sm font-bold truncate">
              {canPlay
                ? `${formatTime(currentTime)} / ${formatTime(duration)}`
                : stillLoading
                  ? t('common.loading', { defaultValue: 'Loading…' })
                  : '—'}
            </span>
          </div>
        </div>
      </div>

      <div className="relative">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.05}
          value={currentTime}
          onChange={(e) => seek(parseFloat(e.target.value))}
          disabled={!canPlay}
          className="w-full accent-amber"
        />
        {duration > 0 && loopA != null && (
          <div
            className="absolute top-1/2 -translate-y-1/2 h-3 bg-amber/30 border-x-2 border-amber pointer-events-none rounded"
            style={{
              left: `${(loopA / duration) * 100}%`,
              width: `${(((loopB ?? loopA) - loopA) / duration) * 100}%`,
            }}
          />
        )}
      </div>

      {canPlay && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            onClick={() => setLoopA(currentTime)}
            className={`px-2.5 py-1 rounded-md border font-mono font-bold uppercase tracking-wider transition-colors ${
              loopA != null
                ? 'bg-amber/15 border-amber/40 text-amber'
                : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:text-white'
            }`}
            title={t('songs.audioSetA', { defaultValue: 'Set loop start' })}
          >
            A {loopA != null && `· ${formatTime(loopA)}`}
          </button>
          <button
            onClick={() => setLoopB(currentTime)}
            disabled={loopA == null}
            className={`px-2.5 py-1 rounded-md border font-mono font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${
              loopB != null
                ? 'bg-amber/15 border-amber/40 text-amber'
                : 'bg-white/[0.02] border-white/10 text-zinc-400 hover:text-white'
            }`}
            title={t('songs.audioSetB', { defaultValue: 'Set loop end' })}
          >
            B {loopB != null && `· ${formatTime(loopB)}`}
          </button>
          {(loopA != null || loopB != null) && (
            <button
              onClick={() => { setLoopA(null); setLoopB(null); }}
              className="px-2 py-1 rounded-md border bg-white/[0.02] border-white/10 text-zinc-400 hover:text-red-300"
              title={t('songs.audioClearLoop', { defaultValue: 'Clear loop' })}
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <div className="flex-1" />
          <Repeat className="w-3.5 h-3.5 text-zinc-500" />
          <span className="text-zinc-500 uppercase tracking-wider">
            {t('songs.audioSpeed', { defaultValue: 'Velocità' })}
          </span>
          <input
            type="range"
            min={0.5}
            max={1.5}
            step={0.05}
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            className="w-24 sm:w-32 accent-amber"
          />
          <span className="font-mono text-amber w-10 text-right">{speed.toFixed(2)}x</span>
        </div>
      )}

      {canPlay && (
        <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${readyKeys.length}, minmax(0, 1fr))` }}>
          {readyKeys.sort((a, b) => STEM_META[a].order - STEM_META[b].order).map(key => {
            const { labelKey, Icon } = STEM_META[key];
            const active = !!stemOn[key];
            const label = showSoloLabel
              ? t('songs.stemFullMix', { defaultValue: 'Mix completo' })
              : t(labelKey, { defaultValue: key[0].toUpperCase() + key.slice(1) });
            return (
              <button
                key={key}
                onClick={() => setStemOn(s => ({ ...s, [key]: !s[key] }))}
                className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-xs font-semibold uppercase tracking-wider transition-colors ${
                  active
                    ? 'bg-amber/15 border-amber/40 text-amber'
                    : 'bg-white/[0.02] border-white/10 text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-zinc-400">
        <span className="shrink-0 w-10">{t('drums.volume', { defaultValue: 'Vol' })}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={Math.round(volume * 100)}
          onChange={(e) => setVolume(Number(e.target.value) / 100)}
          className="flex-1 accent-amber"
        />
        <span className="font-mono w-10 text-right shrink-0">{Math.round(volume * 100)}</span>
      </div>

      {unavailableKeys.length > 0 && (
        <details className="text-xs text-zinc-500">
          <summary className="cursor-pointer flex items-center gap-2 hover:text-zinc-300">
            <AlertCircle className="w-3.5 h-3.5" />
            {t('songs.audioStemsHidden', {
              defaultValue: '{{n}} stem nascosti (vuoti o non disponibili)',
              n: unavailableKeys.length,
            })}
          </summary>
          <ul className="mt-2 pl-5 space-y-0.5 list-disc">
            {unavailableKeys.map(k => (
              <li key={k}>
                <span className="font-mono">{k}</span> ·{' '}
                {stemState[k]?.status === 'empty'
                  ? t('songs.audioStemEmpty', { defaultValue: 'silenzio o file vuoto' })
                  : t('songs.audioStemFailed', { defaultValue: 'caricamento fallito' })}
              </li>
            ))}
          </ul>
        </details>
      )}

      {!canPlay && !stillLoading && (
        <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{t('songs.audioBackingError', { defaultValue: 'Nessuno stem audio disponibile per questo brano.' })}</span>
        </div>
      )}

      {attribution && canPlay && (
        <p className="text-[10px] text-zinc-500 italic">{attribution}</p>
      )}
    </div>
  );
};

export default AudioBackingTrack;
